import '@fontsource/instrument-serif';
import '@fontsource/instrument-serif/400-italic.css';

import Flash from '@/components/flash';
import CaprockMark from '@/components/linear/caprock-mark';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import {
  client_work_path,
  login_path,
  pricing_path,
  privacy_path,
  root_path,
  signup_path,
  templates_path,
  terms_path,
} from '@/rails/routes';
import { Head, Link, usePage } from '@inertiajs/react';
import { useEffect, type ReactNode } from 'react';

const CONTACT_EMAIL = 'lance.foley@caprocklabs.com';
const MAIN_CONTENT_ID = 'main-content';

const FOCUS_RING =
  'rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring';

interface NavItem {
  href: string;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { href: templates_path(), label: 'Templates' },
  { href: pricing_path(), label: 'Pricing' },
  { href: client_work_path(), label: 'Client work' },
  { href: login_path(), label: 'Log in' },
];

interface MarketingLayoutProps {
  children: ReactNode;
  flash: {
    notice?: string;
    error?: string;
  };
  pageTitle?: string;
}

const MarketingLayout = ({ children, flash, pageTitle }: MarketingLayoutProps) => {
  const { props, url } = usePage<{ pageTitle?: string; signupOpen?: boolean }>();
  const title = pageTitle || props.pageTitle || 'CAP Rock Labs';
  const path = url.split(/[?#]/)[0];
  const year = new Date().getFullYear();
  // Every public page says whether signup is open. The button says the same thing the page does.
  const signupLabel = props.signupOpen === false ? 'Request access' : 'Get started';

  // The public site is a light surface. A dark preference left by the workspace must not leak in.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.removeAttribute('data-linear-theme');
  }, []);

  return (
    <>
      <Head title={title} />
      <div data-authority className="bg-background text-foreground flex min-h-screen flex-col">
        <a
          href={`#${MAIN_CONTENT_ID}`}
          className="bg-background sr-only z-50 border px-3 py-2 text-sm focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
        >
          Skip to content
        </a>

        <header className="bg-background border-b sm:sticky sm:top-0 sm:z-10">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-1 px-4 py-3 sm:px-6">
            <Link href={root_path()} className={cn('flex items-center gap-2.5', FOCUS_RING)}>
              <CaprockMark size={28} />
              <span className="authority-display text-lg">CAP Rock Labs</span>
            </Link>

            {/* Under 640px the links drop to a second row, so nothing is hidden behind a menu. */}
            <nav aria-label="Main" className="order-last w-full sm:order-none sm:ml-auto sm:w-auto">
              <ul className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm sm:gap-x-6">
                {NAV_ITEMS.map((item) => {
                  const current = path === item.href;

                  return (
                    <li key={item.href}>
                      <Link
                        href={item.href}
                        aria-current={current ? 'page' : undefined}
                        className={cn(
                          'inline-block py-2 underline-offset-8 transition-colors',
                          FOCUS_RING,
                          current
                            ? 'text-foreground decoration-accent font-medium underline decoration-2'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <Button asChild size="sm" className="ml-auto sm:ml-0">
              <Link href={signup_path()}>{signupLabel}</Link>
            </Button>
          </div>
        </header>

        <main id={MAIN_CONTENT_ID} className="flex-1">
          {children}
        </main>

        <footer className="border-t">
          <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 text-sm sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
            <div className="flex flex-col gap-3 lg:col-span-2">
              <span className="flex items-center gap-2.5">
                <CaprockMark size={28} />
                <span className="authority-display text-lg">CAP Rock Labs</span>
              </span>
              <p className="text-muted-foreground max-w-sm leading-relaxed">
                Describe the app you run your business on and get it built. Lists, photos and files,
                email, payments, and QuickBooks are already in place.
              </p>
              <p className="text-muted-foreground">Litchfield, Minnesota</p>
            </div>

            <nav aria-label="Footer" className="flex flex-col gap-3">
              <h2 className="font-medium">Site</h2>
              <ul className="text-muted-foreground flex flex-col gap-2">
                {NAV_ITEMS.map((item) => (
                  <li key={item.href}>
                    <Link href={item.href} className={cn('hover:text-foreground', FOCUS_RING)}>
                      {item.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link href={signup_path()} className={cn('hover:text-foreground', FOCUS_RING)}>
                    {signupLabel}
                  </Link>
                </li>
              </ul>
            </nav>

            <div className="flex flex-col gap-3">
              <h2 className="font-medium">Contact</h2>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className={cn(
                  'text-muted-foreground hover:text-foreground w-fit break-all',
                  FOCUS_RING
                )}
              >
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>

          <div className="border-t">
            <div className="text-muted-foreground mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-5 text-xs sm:px-6">
              <p>© {year} CAP Rock Labs</p>
              <Link href={privacy_path()} className={cn('hover:text-foreground', FOCUS_RING)}>
                Privacy
              </Link>
              <Link href={terms_path()} className={cn('hover:text-foreground', FOCUS_RING)}>
                Terms
              </Link>
            </div>
          </div>
        </footer>

        <Flash {...flash} />
        <Toaster />
      </div>
    </>
  );
};

export default MarketingLayout;
